import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import { collection, getDocs, query, doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import ArgotPractice from './ArgotPractice.jsx';

// 1. Mapping IDs to Display Names
const COURSE_NAMES = {
  oilgas: 'Oil & Gas',
  banking: 'Banking & Finance',
  logistics: 'Logistics & Freight',
  mining: 'Mining & Geology',
  medical: 'Medical Terminology',
  aviation: 'Aviation',
  aesthetician: 'Aesthetics',
  legal: 'Legal',
  hotel: 'Hospitality',
  cabin: 'Cabin Crew',
};

// 2. Mapping IDs to Database Collection Names
const COURSE_ID_TO_DOC_NAME = {
  oilgas: 'oilgas_terms',
  banking: 'banking_terms',
  logistics: 'logistics_terms',
  mining: 'mining_terms',
  medical: 'medical_terms',
  aviation: 'aviation_terms',
  aesthetician: 'aesthetician_terms',
  legal: 'legal_terms',
  hotel: 'hotel_terms',
  cabin: 'cabin_terms',
};

// 3. ✅ NEW: Mapping IDs to Video Backgrounds (in your public folder)
const COURSE_VIDEOS = {
  banking: '/assets/backgrounds/argot/banking_argot_bkgr.mp4',
  mining: '/assets/backgrounds/argot/miming_argot_bkgr.mp4', // (Keep 'miming' if you haven't renamed the file)

  // New ones - Make sure these filenames match what you uploaded!
  oilgas:       '/assets/backgrounds/argot/oilgas_argot_bkgr.mp4',
  logistics:    '/assets/backgrounds/argot/logistics_argot_bkgr.mp4',
  medical:      '/assets/backgrounds/argot/medical_argot_bkgr.mp4',
  aviation:     '/assets/backgrounds/argot/aviation_argot_bkgr.mp4',
  aesthetician: '/assets/backgrounds/argot/aesthetician_argot_bkgr.mp4',
  legal:        '/assets/backgrounds/argot/legal_argot_bkgr.mp4',
  hotel:        '/assets/backgrounds/argot/hotel_argot_bkgr.mp4',
  cabin:        '/assets/backgrounds/argot/cabin_argot_bkgr.mp4',
};

const ArgotPage = () => {
  const { courseId } = useParams();
  const [user] = useAuthState(auth);

  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [mode, setMode] = useState(null); 
  const [progress, setProgress] = useState({ passedTest: false });

  // --- Fetching Logic (Unchanged) ---
  useEffect(() => {
    const fetchArgot = async () => {
      if (!courseId) { setLoading(false); return; }
      
      const docName = COURSE_ID_TO_DOC_NAME[courseId];
      if (!docName) {
        setLoading(false);
        setError(`Invalid course ID: ${courseId}`);
        return;
      }
      
      try {
        const collectionPath = `glossaries/${docName}/terms`;
        const q = query(collection(db, collectionPath));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setError('No terms found for this course.');
          setTerms([]);
        } else {
          const termList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          termList.sort((a, b) => (a.index || 0) - (b.index || 0));
          setTerms(termList);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading Argot:', err);
        setError(`Failed to load terms.`);
      } finally {
        setLoading(false);
      }
    };
    fetchArgot();
  }, [courseId]);

  useEffect(() => {
    if (!user) return; 
    const fetchProgress = async () => {
      const progressPath = `users/${user.uid}/argot/${courseId}/mastery/progress`;
      const docRef = doc(db, progressPath);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setProgress(docSnap.data());
      else await setDoc(docRef, { passedTest: false });
    };
    fetchProgress();
  }, [user, courseId]);

  const handleSetMode = (newMode) => setMode(newMode);
  const handleFinishSession = () => setMode(null);
  
  const handlePassTest = async () => {
    if (!user) return;
    const progressPath = `users/${user.uid}/argot/${courseId}/mastery/progress`;
    const newProgress = { passedTest: true };
    await setDoc(doc(db, progressPath), newProgress, { merge: true });
    setProgress(newProgress);
    setMode(null);
  };

  const courseName = COURSE_NAMES[courseId] || 'Professional Argot';
  
  // ✅ CHECK: Do we have a video for this course?
  const videoSrc = COURSE_VIDEOS[courseId];

  if (loading) return <div className="text-center py-20 text-indigo-600">Loading...</div>;
  if (error) return <div className="text-center py-20 bg-red-100 p-5">{error}</div>;

  // --- MODE 1: EXERCISE VIEW (Flashcards/Test) ---
  // Note: We intentionally DO NOT show the video here to avoid motion sickness.
  if (mode) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <ArgotPractice
          terms={terms}
          mode={mode}
          onFinish={handleFinishSession}
          onPassTest={handlePassTest}
        />
      </div>
    );
  }

  // --- MODE 2: START MENU (Splash Screen) ---
  return (
    <div className="relative min-h-screen overflow-hidden">
      
      {/* ✅ BACKGROUND VIDEO LAYER */}
      {videoSrc ? (
        <>
          {/* The Video */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute top-0 left-0 w-full h-full object-cover z-0"
          >
            <source src={videoSrc} type="video/mp4" />
          </video>
          
          {/* The Dark Overlay (Critical for readability) */}
          <div className="absolute top-0 left-0 w-full h-full bg-black/60 z-10 backdrop-blur-[2px]"></div>
        </>
      ) : (
        // Fallback if no video exists
        <div className="absolute top-0 left-0 w-full h-full bg-gray-50 z-0"></div>
      )}

      {/* ✅ CONTENT LAYER (z-index higher than overlay) */}
      <div className="relative z-20 max-w-4xl mx-auto px-4 py-8">
        <header className="mb-8 p-6 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl rounded-xl text-white">
          <h1 className="text-4xl font-bold tracking-tight">{courseName} Argot</h1>
          <p className="mt-2 text-lg text-gray-200">Master {terms.length} essential industry terms.</p>
        </header>
        
        <div className="space-y-6">
          {/* Card 1 */}
          <div className="p-6 bg-white/90 backdrop-blur shadow-lg rounded-lg border-l-8 border-indigo-600">
            <h2 className="text-2xl font-semibold text-gray-900">Milestone 1: Recognition</h2>
            <p className="text-gray-600 mt-2">Learn the terms. Flip the cards to see the definitions.</p>
            <button
              onClick={() => handleSetMode('flashcards')}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow hover:bg-indigo-700 transition-all"
            >
              Start Flashcards
            </button>
          </div>
          
          {/* Card 2 */}
          <div className="p-6 bg-white/90 backdrop-blur shadow-lg rounded-lg border-l-8 border-indigo-600">
            <h2 className="text-2xl font-semibold text-gray-900">Milestone 2: Multiple Choice</h2>
            <p className="text-gray-600 mt-2">Test your recognition by matching terms to definitions.</p>
            <button
              onClick={() => handleSetMode('test')}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow hover:bg-indigo-700 transition-all"
            >
              Start Test
            </button>
          </div>
          
          {/* Card 3 */}
          <div className={`p-6 bg-white/90 backdrop-blur shadow-lg rounded-lg border-l-8 ${progress.passedTest ? 'border-green-600' : 'border-gray-400'}`}>
            <h2 className="text-2xl font-semibold text-gray-900">Milestone 3: Term Recall</h2>
            <p className="text-gray-600 mt-2">Produce the terms yourself. Use them in your own sentences.</p>
            <button
              onClick={() => handleSetMode('recall')}
              disabled={!progress.passedTest}
              className={`mt-4 px-6 py-2 font-semibold rounded-lg shadow transition-all ${
                progress.passedTest 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-400 text-gray-100 cursor-not-allowed'
              }`}
            >
              {progress.passedTest ? "Start Recall" : "🔒 Locked"}
            </button>
            {!progress.passedTest && (
              <p className="text-sm text-gray-500 mt-2 italic">Pass the Milestone 2 Test to unlock this.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArgotPage;